import { useCallback } from 'react'

import { Form } from '@tribeplatform/react-components/Form'
import { T } from '@tribeplatform/react-components/i18n'
import {
  useCancelDomainTransfer,
  useCommitDomainTransfer,
  useProbeDomain,
} from '@tribeplatform/react-sdk/hooks'
import { HelperText } from '@tribeplatform/react-ui-kit/HelperText'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'

import { TransferWizardSkeleton } from '../components/TransferWizardSkeleton.js'
import { TransferActions } from '../TransferActions/index.js'
import { DomainTransferStatus } from '../TransferStatus/index.js'

interface Props {
  domain: string
  isRefetchingNetwork: boolean
}

export const TransferInProgress = ({ domain, isRefetchingNetwork }: Props) => {
  const {
    data: probeResult,
    isInitialLoading: isPerformingInitialProbe,
    isFetching: isProbing,
    isError: hasErrorRevalidating,
    refetch: revalidateDns,
  } = useProbeDomain({
    variables: {
      domain,
    },
    useQueryOptions: {
      enabled: !!domain,
      refetchOnWindowFocus: false,
    },
  })

  const {
    mutateAsync: cancelTransfer,
    isLoading: isCanceling,
    isError: hasErrorCancelling,
  } = useCancelDomainTransfer()

  const {
    mutateAsync: commitTransfer,
    isLoading: isCommitting,
    isError: hasErrorCommitting,
  } = useCommitDomainTransfer()

  const isLoadingAny =
    isPerformingInitialProbe || isProbing || isCanceling || isCommitting
  const hasAnyError =
    !isLoadingAny &&
    (hasErrorRevalidating || hasErrorCancelling || hasErrorCommitting)

  const onCommit = useCallback(async () => {
    try {
      await commitTransfer()
      setTimeout(() => {
        window.location.href = `https://${domain}`
      }, 5000)
    } catch (_) {
      // Noop
    }
  }, [commitTransfer, domain])

  return (
    <LoadingOverlay
      isLoading={isPerformingInitialProbe || isRefetchingNetwork}
      pulse
      loadingComponent={<TransferWizardSkeleton />}
    >
      <DomainTransferStatus domainProbeResult={probeResult} />
      <Form.Actions className="mt-5">
        <TransferActions
          domain={domain}
          probeStatus={probeResult?.status}
          onRevalidate={revalidateDns}
          isRevalidating={isProbing}
          onCommit={onCommit}
          isCommitting={
            isCommitting ||
            /**
             * Committing the transfer will invalidate the probe result which should be
             * considered as part of the commit process.
             */
            isProbing
          }
          onCancel={cancelTransfer}
          isCanceling={isCanceling}
        />
      </Form.Actions>
      {hasAnyError && (
        <HelperText status="error">
          <T
            defaultMessage="Something went wrong. Please try again later."
            id="Generics.SomethingWentWrongTryAgainLater"
          />
        </HelperText>
      )}
    </LoadingOverlay>
  )
}
